import { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Input, Spinner, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { crm } from "../../../theme";
import { processStatus } from "./types";
import { getStatusLimits, saveStatusLimits } from "../../../services/notificationService";
import { TableCard, tdProps, thProps } from "../../process/components/tableStyles";

// CANCELADO/ENCERRADO não fazem sentido como limite (processo parado de propósito)
const IGNORED = ["CANCELADO", "ENCERRADO"];

/** Limite de tempo (dias corridos) por status: estourou, admins + responsável recebem no sino. */
const StatusLimits = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});

  const statuses = useMemo(
    () => processStatus.map((s) => s.value).filter((s) => !IGNORED.includes(s)),
    [],
  );

  useEffect(() => {
    getStatusLimits()
      .then((limits) => {
        const map = Object.fromEntries(limits.map((l) => [l.status, l.limitDays ? String(l.limitDays) : ""]));
        setSaved(map);
        setDraft(map);
      })
      .catch(() => toast({ title: "Não foi possível carregar os limites", status: "error" }))
      .finally(() => setLoading(false));
  }, []);

  const dirty = statuses.some((s) => (draft[s] ?? "") !== (saved[s] ?? ""));

  const save = async () => {
    setSaving(true);
    try {
      const payload = statuses
        .filter((s) => (draft[s] ?? "").trim() !== "")
        .map((s) => ({ status: s, limitDays: Number(draft[s]) }));
      const result = await saveStatusLimits(payload);
      if (!result?.success) {
        toast({ title: result?.notifications?.[0] ?? "Não foi possível salvar", status: "error" });
        return;
      }
      setSaved(draft);
      toast({ title: "Limites salvos", status: "success" });
    } catch {
      toast({ title: "Não foi possível salvar", status: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Flex justify="center" py="32px">
        <Spinner />
      </Flex>
    );

  return (
    <Box w="100%">
      <Text fontSize="20px" fontWeight="700" color={crm.text}>
        Limites de status
      </Text>
      <Text color={crm.textMuted} fontSize="sm" mt="4px" mb="16px">
        Dias corridos que um processo pode ficar em cada status. Passou do limite, os admins e o responsável do
        processo recebem um aviso no sino (uma vez por entrada no status). Vazio = sem limite.
      </Text>
      <TableCard>
        <Thead>
          <Tr>
            <Th {...thProps}>Status</Th>
            <Th {...thProps} w="180px">Limite (dias)</Th>
          </Tr>
        </Thead>
        <Tbody>
          {statuses.map((status) => (
            <Tr key={status}>
              <Td {...tdProps}>{status}</Td>
              <Td {...tdProps}>
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  max={3650}
                  step={1}
                  value={draft[status] ?? ""}
                  placeholder="Sem limite"
                  onChange={(e) => setDraft((d) => ({ ...d, [status]: e.target.value.replace(/\D/g, "") }))}
                  aria-label={`Limite em dias para ${status}`}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </TableCard>
      <Flex justify="flex-end" mt="16px" gap="8px">
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(saved)} isDisabled={saving}>
            Descartar
          </Button>
        )}
        <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={save} isLoading={saving} isDisabled={!dirty}>
          Salvar limites
        </Button>
      </Flex>
    </Box>
  );
};

export default StatusLimits;
